import React from 'react';
import { Head, Link } from '@inertiajs/react';
import './Workspace.css';

/**
 * Choose a scene. Where the contract screen leads; the tree with each scene's progress
 * through the seven steps. (The drawer inside a scene is the quick switch; this is the map.)
 */

function Progress({ p, total }) {
  if (!p) return null;
  if (p.state === 'done') return <span className="srs-progress done">done</span>;
  if (p.state === 'not_started') return <span className="srs-progress none">not started</span>;
  return (
    <span className="srs-progress">
      step {p.number} of {total} · {p.verb}
      {p.pending > 0 && <span className="badge text-bg-warning ms-2">{p.pending} to decide</span>}
    </span>
  );
}

function Nodes({ nodes, uuid, total, depth = 0 }) {
  return (
    <ul className={`srs-scenes-list depth-${depth}`}>
      {nodes.map((n) => (
        <li key={n.id} className={`grain-${n.grain}`}>
          {n.grain === 'scene' || n.grain === 'span' ? (
            <Link href={`/srs/p/${uuid}/${n.id}`} className="srs-scene-row">
              <span className="srs-scene-label">{n.label}</span>
              <Progress p={n.progress} total={total} />
              {n.counts.blocking > 0 && <span className="badge text-bg-danger ms-2">{n.counts.blocking} blocking</span>}
            </Link>
          ) : n.grain === 'script' ? null : (
            <div className="srs-scene-group">{n.label}</div>
          )}
          {n.children?.length > 0 && <Nodes nodes={n.children} uuid={uuid} total={total} depth={n.grain === 'script' ? depth : depth + 1} />}
        </li>
      ))}
    </ul>
  );
}

export default function Scenes({ project, tree, contract, total_steps }) {
  // First scene that isn't done, for the one-click path.
  const flat = [];
  const walk = (nodes) => nodes.forEach((n) => { if (n.grain === 'scene') flat.push(n); if (n.children) walk(n.children); });
  walk(tree);
  const nextUp = flat.find((s) => s.progress?.state === 'in_progress') || flat.find((s) => s.progress?.state === 'not_started');
  const done = flat.filter((s) => s.progress?.state === 'done').length;

  return (
    <div className="srs srs-work container py-3" style={{ maxWidth: 980 }}>
      <Head title={`Scenes · ${project.title || 'Script'}`} />
      <div className="d-flex flex-wrap align-items-center gap-3">
        <Link href="/srs" className="text-muted small">‹ Scripts</Link>
        <Link href={`/srs/p/${project.uuid}/contract`} className="text-muted small">{project.title} · contract ({contract.approved}/{contract.count} approved)</Link>
      </div>
      <h1 className="h4 mt-2 mb-1">Choose a scene</h1>
      <p className="text-muted mb-3">
        Each scene goes through the same seven steps. Opening a scene for the first time starts step 1 — reading it as written — on its own.
        {flat.length > 0 && <> {done} of {flat.length} done.</>}
      </p>

      {nextUp && (
        <div className="srs-now mb-3 d-flex flex-wrap align-items-center gap-3">
          <div className="flex-grow-1">
            <div className="srs-step-no">{nextUp.progress.state === 'in_progress' ? 'Pick up where you left off' : 'Next up'}</div>
            <div className="srs-now-title">{nextUp.label}</div>
            <div className="srs-now-why"><Progress p={nextUp.progress} total={total_steps} /></div>
          </div>
          <Link href={`/srs/p/${project.uuid}/${nextUp.id}`} className="btn btn-primary btn-lg">
            {nextUp.progress.state === 'in_progress' ? 'Continue' : 'Start this scene'} →
          </Link>
        </div>
      )}

      <Nodes nodes={tree} uuid={project.uuid} total={total_steps} />
    </div>
  );
}
